import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Play, TrendingUp, Shield, Users, Award, ArrowRight } from 'lucide-react';

const stats = [
  { label: 'Win Rate Increase', value: '+40%', icon: TrendingUp },
  { label: 'Compliance Score', value: '99%', icon: Shield },
  { label: 'Time Saved', value: '60%', icon: Users },
  { label: 'Customer Satisfaction', value: '4.9/5', icon: Award },
];

export function Hero() {
  const [showVideo, setShowVideo] = useState(false);

  return (
    <div className="relative min-h-[calc(100vh-64px)] bg-gradient-to-br from-purple-50 via-white to-purple-50">
      {/* Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -left-1/4 top-1/4 h-[500px] w-[500px] rounded-full bg-purple-200/50 blur-3xl"></div>
        <div className="absolute -right-1/4 bottom-1/4 h-[500px] w-[500px] rounded-full bg-purple-300/50 blur-3xl"></div>
        <div className="absolute left-1/2 top-1/4 h-[300px] w-[300px] -translate-x-1/2 rounded-full bg-purple-400/30 blur-3xl"></div>
      </div>

      {/* Content */}
      <div className="relative mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <div className="animate-fade-in-up">
            {/* Logo and Brand */}
            <div className="mb-8 flex items-center justify-center space-x-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-purple-600 to-purple-800 shadow-xl">
                <img src="/logo.svg" alt="Bidzora" className="h-10 w-10" />
              </div>
              <h1 className="bg-gradient-to-r from-purple-600 to-purple-900 bg-clip-text text-5xl font-extrabold text-transparent">
                Bidzora
              </h1>
            </div>

            {/* Main Heading */}
            <h2 className="text-5xl font-bold tracking-tight text-gray-900 sm:text-6xl lg:text-7xl">
              Transform Your{' '}
              <span className="relative">
                <span className="relative z-10 bg-gradient-to-r from-purple-600 to-purple-900 bg-clip-text px-2 text-transparent">
                  Government
                </span>
                <span className="absolute inset-x-0 bottom-0 z-0 h-4 skew-x-3 bg-purple-100"></span>
              </span>
              <br />
              Contracting Process
            </h2>

            {/* Subtitle */}
            <p className="mt-8 text-xl leading-8 text-gray-600 sm:text-2xl">
              Win more contracts with AI-powered insights, automated compliance,
              <br className="hidden sm:block" />
              and seamless team collaboration.
            </p>

            {/* CTA Buttons */}
            <div className="mt-12 flex items-center justify-center gap-x-6">
              <Link
                to="/auth/signup"
                className="group relative inline-flex items-center justify-center overflow-hidden rounded-xl bg-gradient-to-r from-purple-600 to-purple-800 px-8 py-4 text-lg font-semibold text-white shadow-xl transition-all duration-200 hover:shadow-purple-500/25"
              >
                <span className="absolute -end-full transition-all group-hover:end-4">
                  <ArrowRight className="h-6 w-6" />
                </span>
                <span className="transition-all group-hover:me-4">Get Started Free</span>
              </Link>
              <button 
                onClick={() => setShowVideo(true)}
                className="flex items-center space-x-3 text-lg font-semibold text-gray-700 transition-colors hover:text-purple-600"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-100 transition-colors hover:bg-purple-200">
                  <Play className="h-5 w-5" />
                </div>
                <span>Watch Demo</span>
              </button>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="mt-20 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {stats.map((stat) => {
              const Icon = stat.icon;
              return (
                <div
                  key={stat.label}
                  className="group relative overflow-hidden rounded-2xl bg-white p-8 shadow-xl ring-1 ring-purple-900/5 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
                >
                  <div className="relative z-10 flex flex-col items-center">
                    <div className="rounded-xl bg-purple-100 p-3">
                      <Icon className="h-8 w-8 text-purple-600" />
                    </div>
                    <p className="mt-4 text-3xl font-bold text-gray-900">{stat.value}</p>
                    <p className="mt-2 text-sm text-gray-600">{stat.label}</p>
                  </div>
                  <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-purple-50 opacity-0 transition-all duration-300 group-hover:opacity-100"></div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {showVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm">
          <div className="relative w-full max-w-5xl">
            <button
              onClick={() => setShowVideo(false)}
              className="absolute -right-4 -top-4 rounded-full bg-white p-2 text-gray-900 shadow-lg hover:bg-gray-100"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div className="aspect-video rounded-2xl bg-black shadow-2xl">
              <iframe
                className="h-full w-full rounded-2xl"
                src="https://www.youtube.com/embed/dQw4w9WgXcQ"
                title="Product Demo"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}